import { useGetMyExamCentersQuery } from '@exam-center/graphql';
import { Navigate } from '@exam-center/router';
import { useAuth } from '@shared/auth';
import { Loading } from '@shared/components';
import { type PropsWithChildren } from '@shared/utils';
import { useMemo } from 'react';

interface ExamCenterMemberGuardProps extends PropsWithChildren {
  examCenterCode: string;
}

export default function ExamCenterMemberGuard(props: Readonly<ExamCenterMemberGuardProps>) {
  const { examCenterCode, children } = props;

  const { userProfile } = useAuth();

  const {
    data: myExamCentersData,
    loading: isLoading,
    error,
  } = useGetMyExamCentersQuery({
    skip: !userProfile?.id,
  });

  const myExamCenters = useMemo(
    () => myExamCentersData?.ecGetMyExamCenters.examCenters ?? [],
    [myExamCentersData],
  );

  const isAdmin = userProfile?.isAdmin ?? false;

  const isExamCenterDataLoaded = isLoading && !myExamCentersData;

  const currentExamCenter = useMemo(() => {
    return myExamCenters.find(
      (examCenter) => examCenter.code.toLowerCase() === examCenterCode?.toLowerCase(),
    );
  }, [myExamCenters, examCenterCode]);

  if (isLoading) {
    return <Loading sx={{ height: '100%' }} />;
  }

  if (error) {
    return <Navigate to='/ec-not-found' replace />;
  }

  // Admin can access every exam centers
  if (!isAdmin && !isExamCenterDataLoaded && !currentExamCenter) {
    return <Navigate to='/ec-not-found' replace />;
  }

  return children;
}
